import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Field } from 'formik';
import Button from '../button';
import ErrorMessage from '../form-elements/field-message/error-message';
import {
  formatDonationContent,
  getDonationFrequency,
  getDonationAmount,
  convertFrequencies,
} from './helpers';
import DonationForm from '../donation-form-handler';
import { sendEvent } from '../../utils/analytics';

// Styles
import {
  Hero,
  Title,
  InputBox,
  DonateSubmit,
  OwnAmountField,
  StyledButton,
  ButtonContainer,
  Description,
  StyledContainer,
  Image,
  ImageWrapper,
  StyledLabel,
} from './styles';
import { consistentString } from '../../utils/content-formatting';

const DonationHeroWithButtons = ({ content }) => {
  const formattedValues = formatDonationContent(content);

  const {
    heading,
    campaignId,
    defaultDonationFrequency,
    donationHeroImage,
    defaultSingleDonationItem,
    defaultMonthlyDonationItem,
  } = content;

  // State for frequency button single / monthly
  const [selectedFrequency, setSelectedFrequency] = useState(
    consistentString(
      !defaultDonationFrequency ? 'single' : defaultDonationFrequency
    )
  );

  const amountFromParams = getDonationAmount();

  // Pre selected amounts for frequency
  const [activeOption, setActiveOption] = useState({
    single:
      formattedValues.single[
        Math.min(
          defaultSingleDonationItem ? defaultSingleDonationItem - 1 : 2,
          formattedValues.single.length - 1
        )
      ],
    monthly:
      formattedValues.monthly[
        Math.min(
          defaultMonthlyDonationItem ? defaultMonthlyDonationItem - 1 : 2,
          formattedValues.monthly.length - 1
        )
      ],
  });

  const [amountSelectionMethod, setAmountSelectionMethod] = useState('buttons');
  const [ownAmountValue, setOwnAmountValue] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    const donationFrequency = consistentString(
      getDonationFrequency(defaultDonationFrequency)
    );

    setSelectedFrequency(donationFrequency);

    if (amountFromParams) {
      const amountOption = formattedValues[donationFrequency].find(
        ({ amount }) => amount === amountFromParams
      );

      if (amountOption) {
        setActiveOption((currentValues) => ({
          ...currentValues,
          [donationFrequency]: amountOption,
        }));
      } else {
        setAmountSelectionMethod('own amount');
        setOwnAmountValue(amountFromParams);
      }
    } else {
      setActiveOption({
        single:
          formattedValues.single[
            Math.min(
              defaultSingleDonationItem ? defaultSingleDonationItem - 1 : 2,
              formattedValues.single.length - 1
            )
          ],
        monthly:
          formattedValues.monthly[
            Math.min(
              defaultMonthlyDonationItem ? defaultMonthlyDonationItem - 1 : 2,
              formattedValues.monthly.length - 1
            )
          ],
      });
    }
  }, [content]);

  // Fail-safe if there is an issue with retrieving the donation options
  if (!formattedValues) return null;

  const { amount: activeAmount, description: activeDescription } =
    activeOption[selectedFrequency];

  const frequency = consistentString(
    convertFrequencies(getDonationFrequency(selectedFrequency))
  );

  const onAmountChange = (value) => {
    // Analytics
    sendEvent({
      event: 'donationAction',
      donationValue: value,
      donationFrequency: selectedFrequency,
      donationImage: donationHeroImage,
      interactType: 'Donation Button',
    });

    // Check the option value chosen exists, arrow navigation currently
    // does not pass through a correct value to the event handler
    const chosenOption = formattedValues[selectedFrequency].find(
      (option) => option.amount === value
    );
    if (chosenOption) {
      setActiveOption((currentValues) => ({
        ...currentValues,
        [selectedFrequency]: chosenOption,
      }));
    }
  };

  const getFrequencyButtonStyle = (buttonFrequency) =>
    buttonFrequency === selectedFrequency
      ? 'secondary-standard'
      : 'secondary-outline';

  const getAmountButtonStyle = (buttonAmount) => {
    if (amountSelectionMethod === 'own amount') {
      return 'secondary-outline';
    }

    if (buttonAmount === activeAmount) {
      return 'secondary-standard';
    }

    return 'secondary-outline';
  };

  const getAmountLabel = (buttonAmount, buttonIndex, noOfButtons) => {
    const buttonState =
      buttonAmount === activeAmount ? 'selected' : 'unselected';

    return `£${buttonAmount}, ${buttonState}, ${buttonIndex} of ${noOfButtons}`;
  };

  const handleFrequencySelect = (buttonFrequency, setFieldValue) => {
    if (amountSelectionMethod === 'buttons') {
      setFieldValue('amount-holder', activeOption[buttonFrequency].amount);
    }

    setFieldValue('frequency', convertFrequencies(buttonFrequency));
    setSelectedFrequency(buttonFrequency);

    sendEvent({
      event: 'donationTabSelect',
      frequency: buttonFrequency,
    });
  };

  return (
    <Hero>
      <ImageWrapper>
        <Image $image={donationHeroImage} />
        <StyledContainer>
          <InputBox>
            <Title>{heading}</Title>
            <DonationForm
              defaultDonationValue={amountFromParams || activeAmount}
              campaignId={campaignId}
              inline={false}
              frequency={frequency}
              id="donation-hero-form"
              dataTracking={{
                donationImage: donationHeroImage && donationHeroImage.file.url,
                activeDescription,
                donationBespoke: amountSelectionMethod === 'own amount',
              }}
              render={({ setFieldValue }) => (
                <>
                  <ButtonContainer>
                    <StyledButton
                      bg={getFrequencyButtonStyle('single')}
                      onClick={() => {
                        handleFrequencySelect('single', setFieldValue);
                      }}
                    >
                      Donate once
                    </StyledButton>
                    <StyledButton
                      bg={getFrequencyButtonStyle('monthly')}
                      onClick={() => {
                        handleFrequencySelect('monthly', setFieldValue);
                      }}
                    >
                      Donate monthly
                    </StyledButton>
                  </ButtonContainer>

                  {activeDescription ? (
                    <Description>{activeDescription}</Description>
                  ) : null}

                  <ButtonContainer>
                    {formattedValues[selectedFrequency].map((amount, index) => (
                      <StyledButton
                        bg={getAmountButtonStyle(amount.amount)}
                        key={index}
                        onClick={() => {
                          setFieldValue('amount-holder', amount.amount);
                          onAmountChange(amount.amount);
                          setAmountSelectionMethod('buttons');
                          setError(null);
                          setOwnAmountValue('');
                        }}
                        aria-label={getAmountLabel(
                          amount.amount,
                          index + 1,
                          formattedValues[selectedFrequency].length
                        )}
                      >
                        £{amount.amount}
                      </StyledButton>
                    ))}
                  </ButtonContainer>

                  <StyledLabel htmlFor="own-amount-holder">
                    Choose your own amount
                  </StyledLabel>

                  <Field name="own-amount-holder">
                    {({ meta, ...rest }) => (
                      <OwnAmountField
                        $noMargin
                        $inline
                        {...rest}
                        id="own-amount-holder"
                        value={ownAmountValue}
                        min="1"
                        step="1"
                        $error={error}
                        onChange={(e) => {
                          setFieldValue('amount-holder', e.target.value);
                          setError(null);
                          setOwnAmountValue(e.target.value);
                          setAmountSelectionMethod('own amount');
                        }}
                      />
                    )}
                  </Field>

                  {error ? <ErrorMessage message={error} /> : null}
                  <DonateSubmit
                    as={Button}
                    bg="primary"
                    type="submit"
                    onClick={(e) => {
                      if (
                        amountSelectionMethod === 'own amount' &&
                        ownAmountValue === ''
                      ) {
                        e.preventDefault();
                        setError('Please enter a valid amount');
                      }
                    }}
                    aria-label={`Donate £${
                      amountSelectionMethod === 'buttons'
                        ? activeAmount
                        : ownAmountValue
                    } ${frequency === 'once' ? frequency : 'monthly'}`}
                  >
                    Donate now
                  </DonateSubmit>
                </>
              )}
            />
          </InputBox>
        </StyledContainer>
      </ImageWrapper>
    </Hero>
  );
};

DonationHeroWithButtons.propTypes = {
  content: PropTypes.shape({
    heading: PropTypes.string.isRequired,
    campaignId: PropTypes.number,
    donationHeroImage: PropTypes.object.isRequired,
    singleAmount1: PropTypes.number.isRequired,
    singleDescription1: PropTypes.string,
    singleAmount2: PropTypes.number.isRequired,
    singleDescription2: PropTypes.string,
    singleAmount3: PropTypes.number,
    singleDescription3: PropTypes.string,
    singleAmount4: PropTypes.number,
    singleDescription4: PropTypes.string,
    monthlyAmount1: PropTypes.number.isRequired,
    monthlyDescription1: PropTypes.string,
    monthlyAmount2: PropTypes.number.isRequired,
    monthlyDescription2: PropTypes.string,
    monthlyAmount3: PropTypes.number,
    monthlyDescription3: PropTypes.string,
    monthlyAmount4: PropTypes.number,
    monthlyDescription4: PropTypes.string,
    defaultDonationFrequency: PropTypes.string.isRequired,
    defaultSingleDonationItem: PropTypes.number,
    defaultMonthlyDonationItem: PropTypes.number,
  }),
};

export default DonationHeroWithButtons;
